import { Box, BoxProps, Popover } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useRef, useState } from 'react';

const CLOSE_DELAY_MS = 150;

export function HoverOnlyTooltipComponent({
  content,
  onClose,
  children,
  displayedContainerStyle,
  tooltipContainerStyle,
  contentStyle,
  withPortal = false,
}: {
  content: React.ReactNode | string | null;
  onClose: () => void;
  children: any;
  displayedContainerStyle?: BoxProps;
  tooltipContainerStyle?: BoxProps;
  contentStyle?: BoxProps;
  /**
   * Render the popup in a portal on document.body with the `popover` z-index token.
   * Required when the trigger sits among elements with their own stacking contexts
   * (e.g. DSM inputs, whose inset labels/values otherwise paint over the popup).
   */
  withPortal?: boolean;
}) {
  const hasContent = !!content;
  const [open, setIsOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleOpen = useCallback(() => {
    clearTimeout(closeTimer.current);
    setIsOpen(true);
  }, []);

  const handleScheduleClose = useCallback(() => {
    closeTimer.current = setTimeout(() => {
      setIsOpen(false);
      onClose();
    }, CLOSE_DELAY_MS);
  }, [onClose]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const handleClickOutside = useCallback(() => {
    clearTimeout(closeTimer.current);
    setIsOpen(false);
    onClose();
  }, [onClose]);

  const popupCalculatedContainerStyle = {
    background: 'white',
    padding: '1em',
    fontSize: '16px',
    textAlign: 'left',
    color: '#44474B',
    ...(tooltipContainerStyle || {}),
  };
  const contentContainerProps = {
    padding: '0',
    width: '490px',
    shadow: 'lg',
    radius: 'sm',
    overflow: 'hidden',
    background: 'white',
    // The popover z-index token (9000) beats the modal token (8000), so a portaled
    // popup stays visible even when the trigger lives inside a modal.
    ...(withPortal ? { zIndex: 'popover' } : {}),
    ...(contentStyle || {}),
  };
  const portalProps = withPortal ? { withPortal: true as const, portalTarget: document.body } : {};
  return (
    <Popover
      {...portalProps}
      content={
        <Box
          as="div"
          style={(popupCalculatedContainerStyle || {}) as any}
          className="tooltip-container"
          onMouseEnter={handleOpen}
          onMouseLeave={handleScheduleClose}
        >
          {content}
        </Box>
      }
      isOpen={!hasContent ? false : open}
      placement="bottom-end"
      offsetFromTarget={8}
      contentContainerProps={contentContainerProps as any}
      onClickOutside={handleClickOutside}
    >
      <Box
        onMouseEnter={handleOpen}
        onMouseLeave={handleScheduleClose}
        style={(displayedContainerStyle || {}) as any}
      >
        {children}
      </Box>
    </Popover>
  );
}
